import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOKEN_KEY = "ht_token";
const MSG_KEY = "ht_session_msg";
const NO_RETRY = ["/auth/login", "/auth/refresh", "/auth/logout"];

const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const getToken = () => localStorage.getItem(TOKEN_KEY) || "";
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);

export function tokenExpiresAt() {
  try {
    const part = getToken().split(".")[1];
    return JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/"))).exp * 1000;
  } catch {
    return 0;
  }
}

let expiredFired = false;
export const resetSessionExpired = () => {
  expiredFired = false;
};

export function endSession(message) {
  if (expiredFired) return;
  expiredFired = true;
  const hadSession = !!localStorage.getItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_KEY);
  if (hadSession && message) sessionStorage.setItem(MSG_KEY, message);
  window.dispatchEvent(new Event("ht:session-expired"));
}

export function takeSessionMessage() {
  const m = sessionStorage.getItem(MSG_KEY);
  sessionStorage.removeItem(MSG_KEY);
  return m;
}

let refreshing = null;
export function refreshSession() {
  if (!refreshing) {
    refreshing = axios
      .post(`${API}/auth/refresh`, {}, { withCredentials: true })
      .then((r) => {
        setToken(r.data.access_token);
        return true;
      })
      .catch((e) => {
        const s = e?.response?.status;
        if (s === 401 || s === 403) {
          endSession(apiError(e, "Your session has expired. Please sign in again."));
          return false;
        }
        throw e;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

const pending = () => new Promise(() => {});

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const { config, response } = error;
    const url = config?.url || "";
    if (!response || NO_RETRY.some((p) => url.includes(p))) throw error;
    if (response.status === 403 && String(response.data?.detail || "").startsWith("Account deactivated")) {
      endSession(response.data.detail);
      return pending();
    }
    if (response.status !== 401) throw error;
    if (config._retried) {
      endSession("Your session has expired. Please sign in again.");
      return pending();
    }
    const ok = await refreshSession();
    if (!ok) return pending();
    config._retried = true;
    return api(config);
  }
);

export const fileUrl = (path) =>
  `${API}/files/${path}?auth=${encodeURIComponent(getToken())}`;

export const productImage = (p) =>
  p.photo_path ? fileUrl(p.photo_path) : p.photo_url || null;

export function apiError(e, fallback = "Something went wrong. Please try again.") {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((x) => (x && typeof x.msg === "string" ? x.msg : JSON.stringify(x))).join(" ");
  return fallback;
}

export default api;
